import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { ClipboardList, Loader2, Check, X, Trophy, RotateCcw, ArrowRight, ArrowLeft, Lock, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const StudentMCQPage = () => {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const res = await api.get(`/tests?module_type=mcq`);
      console.log('[StudentMCQPage] Fetched tests:', res.data);
      console.log('[StudentMCQPage] Number of tests:', res.data.length);
      setTests(res.data);
    } catch (err) {
      console.error('[StudentMCQPage] Error fetching tests:', err);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (testId) => {
    try {
      const res = await api.get(`/tests/${testId}`);
      const testData = res.data;
      // Parse options for each question and shuffle them
      const processedQuestions = testData.questions.map(q => {
        const options = Array.isArray(q.options) ? q.options : 
                       (typeof q.options === 'string' ? JSON.parse(q.options) : []);
        const correctAnswer = options[q.correct_option];
        const shuffled = [...options].sort(() => Math.random() - 0.5);
        return { ...q, options: shuffled, _correctAnswer: correctAnswer };
      });
      setQuestions(processedQuestions);
      setSelectedTestId(testId);
      setCurrentIndex(0);
      setAnswers({});
      setSubmitted(false);
      setResult(null);
    } catch (err) {
      toast.error('Failed to load quiz');
    }
  };

  const selectAnswer = (questionId, optionIndex) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionId]: optionIndex }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = questions.map(q => {
        const selectedIndex = answers[q.id];
        const selectedOptionText = q.options[selectedIndex];
        return {
          question_id: q.id,
          answer: selectedOptionText ?? ''  // send option text so backend grades correctly
        };
      });

      const res = await api.post('/quiz/submit', formattedAnswers);
      setResult(res.data);
      setSubmitted(true);
    } catch (err) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuiz = () => {
    setAnswers({});
    setSubmitted(false);
    setResult(null);
    setCurrentIndex(0);
    if (selectedTestId) {
      fetchQuestions(selectedTestId);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(answers).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Show test cards if no test is selected
  if (!selectedTestId) {
    if (tests.length === 0) {
      return (
        <DashboardLayout>
          <div className="max-w-2xl mx-auto text-center py-12">
            <HelpCircle className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              No MCQ Tests Available
            </h2>
            <p className="text-slate-500">
              Your teacher hasn't assigned any MCQ tests yet.
            </p>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              MCQ Quiz Tests
            </h1>
            <p className="text-slate-500">
              Test your knowledge with multiple choice questions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map((test) => {
              const isLocked = !!test.is_locked;
              return (
              <Card key={test.id} className={`module-card h-full group transition-shadow ${isLocked ? 'opacity-75' : 'cursor-pointer hover:shadow-lg'}`} onClick={() => !isLocked && fetchQuestions(test.id)}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                      {test.test_number}
                    </span>
                    {isLocked && (
                      <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                        🔒 Locked
                      </span>
                    )}
                  </div>
                  <div className="inline-flex p-3 rounded-xl bg-sky-100 dark:bg-sky-900/30 mb-3 group-hover:scale-110 transition-transform">
                    <ClipboardList className="h-6 w-6 text-sky-600 dark:text-sky-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                    {test.test_name}
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">{test.question_count || 0} questions</p>
                  {isLocked ? (
                    <p className="text-xs text-amber-600 mt-2">This test is locked. You cannot attempt it.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-slate-500">Attempts: {test.attempts || 0}</span>
                        <span className={`font-semibold ${(test.best_score || 0) >= 80 ? 'text-emerald-600' : (test.best_score || 0) >= 50 ? 'text-amber-600' : 'text-slate-600'}`}>
                          Best: {test.best_score || 0}%
                        </span>
                      </div>
                      <div className="mt-4 flex items-center text-sky-600 dark:text-sky-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                        Start Quiz <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (questions.length === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <HelpCircle className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            No Questions Available
          </h2>
          <p className="text-slate-500">
            Failed to load questions for this test.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // Show result screen
  if (submitted && result) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
          <Card className="overflow-hidden">
            <div className={`p-8 text-center ${
              result.percentage >= 80 ? 'bg-emerald-500' :
              result.percentage >= 50 ? 'bg-amber-500' :
              'bg-rose-500'
            }`}>
              <Trophy className="h-16 w-16 mx-auto text-white mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Quiz Complete!</h2>
              <p className="text-white/90 text-lg">
                You scored {result.correct_count} out of {result.total_questions}
              </p>
            </div>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <p className="text-5xl font-bold text-slate-900 dark:text-white mb-2">
                  {result.percentage}%
                </p>
                <p className={`text-xl ${
                  result.percentage >= 80 ? 'text-emerald-600' :
                  result.percentage >= 50 ? 'text-amber-600' :
                  'text-rose-600'
                }`}>
                  {result.percentage >= 80 ? 'Excellent!' :
                   result.percentage >= 50 ? 'Good job!' :
                   'Keep practicing!'}
                </p>
              </div>

              {/* Results breakdown */}
              <div className="space-y-4 mb-8">
                <h3 className="font-semibold text-slate-900 dark:text-white">Review Answers:</h3>
                {questions.map((q, idx) => {
                  const resultItem = result.results.find(r => r.question_id === q.id);
                  const isCorrect = resultItem?.is_correct;
                  return (
                    <div 
                      key={q.id} 
                      className={`p-4 rounded-xl border-2 ${
                        isCorrect ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20' :
                        'border-rose-200 bg-rose-50 dark:bg-rose-900/20'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-1 rounded-full ${isCorrect ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                          {isCorrect ? (
                            <Check className="h-4 w-4 text-white" />
                          ) : (
                            <X className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 dark:text-white mb-2">
                            {idx + 1}. {q.question_text}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-300">
                            Your answer: {resultItem?.your_answer ?? '—'}
                            {!isCorrect && resultItem?.correct_answer != null && (
                              <span className="text-emerald-600 ml-2">
                                (Correct: {resultItem.correct_answer})
                              </span>
                            )}
                          </p>
                          {resultItem?.explanation && (
                            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                <span className="font-semibold">Explanation: </span>{resultItem.explanation}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-center">
                <Button onClick={resetQuiz} className="bg-indigo-600 hover:bg-indigo-700" data-testid="retry-quiz-btn">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedTestId(null);
              setQuestions([]);
              setCurrentIndex(0);
              setAnswers({});
              setSubmitted(false);
              setResult(null);
            }}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tests
          </Button>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            MCQ Quiz
          </h1>
          <p className="text-slate-500">
            Answer all questions and submit to see your score
          </p>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-slate-500">
            <span>Question {currentIndex + 1} of {questions.length}</span>
            <span>{answeredCount} answered</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card>
          <CardContent className="p-8">
            <div className="mb-6">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300">
                Question {currentIndex + 1}
              </span>
            </div>
            
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
              {currentQuestion?.question_text}
            </h2>

            <div className="space-y-3">
              {currentQuestion?.options.map((option, idx) => {
                const isSelected = answers[currentQuestion.id] === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => selectAnswer(currentQuestion.id, idx)}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                      isSelected 
                        ? 'quiz-option-selected' 
                        : 'quiz-option'
                    }`}
                    data-testid={`option-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                        isSelected 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </div>
                      <span className="text-slate-900 dark:text-white">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Navigation & Submit */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentIndex(prev => prev - 1)}
              disabled={currentIndex === 0}
              data-testid="prev-question-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentIndex(prev => prev + 1)}
              disabled={currentIndex === questions.length - 1}
              data-testid="next-question-btn"
            >
              Next
            </Button>
          </div>
          
          {answeredCount === questions.length && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-quiz-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Submit Quiz
            </Button>
          )}
        </div>

        {/* Question Navigation Pills */}
        <div className="flex flex-wrap gap-2 justify-center">
          {questions.map((q, idx) => {
            const isAnswered = answers[q.id] !== undefined;
            const isCurrent = idx === currentIndex;
            return (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-10 h-10 rounded-full font-medium transition-all ${
                  isCurrent 
                    ? 'bg-indigo-600 text-white' 
                    : isAnswered 
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200'
                }`}
                data-testid={`question-nav-${idx}`}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentMCQPage;
